/* @flow */
import React from "react";
import { Icon, Popup } from "semantic-ui-react";
import { useHistory } from "react-router-dom";

import BlockEdit from "../edit";
import { RoleRequired } from "../../auth/role-required";
import { BlockConfigDash } from "../../../components/block/block-config-dash";
import { TicketEdit } from "../../ticket/edit";

import { reverse, routes } from "../../../routes";
import styled from "styled-components";
import { permissions } from "../../../constant/permissions";
import { t } from "../../../constant/translations";
import dayjs from "dayjs";

type Props = {
  block: Object,
  estates: Array<Object>,
};

const OwnerSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const OwnerInfo = styled.div``;

const OwnerName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1c1917;
`;

const OwnerEmail = styled.a`
  font-size: 13px;
  color: #78716c;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const FacilityManagerSection = styled.div`
  margin-top: 10px;
`;

const FacilityManagerLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a8a29e;
  margin-bottom: 4px;
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 -16px;
  border-top: 1px solid #e7e5e4;
  border-bottom: 1px solid #e7e5e4;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 13px;
  color: #44403c;
  cursor: pointer;
  &:hover {
    background: #f5f5f4;
    color: #1c1917;
  }
`;

const NavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  flex-shrink: 0;
  margin-right: 10px;
  color: #a8a29e;
  & i.icon {
    margin: 0 !important;
    font-size: 13px !important;
    line-height: 1 !important;
    height: 13px !important;
    width: auto !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`;

const NavBadge = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  color: #78716c;
  background: #e7e5e4;
  border-radius: 10px;
  padding: 1px 7px;
`;

const ConfigSection = styled.div`
  padding: 12px 0 4px;
  border-bottom: 1px solid #e7e5e4;
  display: flex;
  justify-content: center;
`;

const SignalSection = styled.div`
  padding: 12px 0 0;
`;

export const DetailsSummary = ({ block, estates }: Props) => {
  const history = useHistory();
  const landlord = block.landlord;
  const facilityManager = block.facility_manager;
  const hasDebt =
    estates &&
    estates.some((estate) => {
      let debtDays = 0;
      if (estate.oldest_charge_date) {
        debtDays = dayjs().diff(dayjs(estate.oldest_charge_date), "days");
      }
      return (
        debtDays > 31 &&
        parseFloat(estate.debt) > 0 &&
        parseFloat(estate.balance) === 0
      );
    });
  const signalsEnabled = block.config?.board?.signals_enabled;
  const signalsInfo = () => {
    if (hasDebt) {
      return t("block.signals_info_customer_has_debt");
    }
    return t("block.signals_info");
  };

  const goTo = (url) => () => {
    history.push(url);
  };

  return (
    <div>
      <OwnerSection>
        <OwnerInfo>
          <OwnerName>{landlord.name}</OwnerName>
          <OwnerEmail href={`mailto:${landlord.email}`}>
            {landlord.email}
          </OwnerEmail>
          <RoleRequired managerOnly={true}>
            {facilityManager && (
              <FacilityManagerSection>
                <FacilityManagerLabel>
                  {t("block.facility_manager")}
                </FacilityManagerLabel>
                <OwnerName>{facilityManager.name}</OwnerName>
                <OwnerEmail href={`mailto:${facilityManager.email}`}>
                  {facilityManager.email}
                </OwnerEmail>
              </FacilityManagerSection>
            )}
          </RoleRequired>
        </OwnerInfo>
        <RoleRequired admin={true} permission={permissions.blockEdit}>
          <BlockEdit entity={block} />
        </RoleRequired>
      </OwnerSection>

      <NavList>
        <NavItem
          onClick={goTo(reverse(routes.blockEstateList, { blockId: block.id }))}
        >
          <NavIcon>
            <Icon name={"building"} />
          </NavIcon>
          {t("side.properties")}
          <NavBadge>{block.estates}</NavBadge>
        </NavItem>
        <RoleRequired staff={true} permission={permissions.customerView}>
          <NavItem
            onClick={goTo(reverse(routes.blockProfiles, { blockId: block.id }))}
          >
            <NavIcon>
              <Icon name={"users"} />
            </NavIcon>
            {t("block.clients")}
          </NavItem>
        </RoleRequired>
        <NavItem
          onClick={goTo(reverse(routes.blockBudget, { blockId: block.id }))}
        >
          <NavIcon>
            <Icon name={"euro sign"} />
          </NavIcon>
          {t("side.accounting")}
        </NavItem>
        <NavItem
          onClick={goTo(reverse(routes.blockTickets, { blockId: block.id }))}
        >
          <NavIcon>
            <Icon name={"bell"} />
          </NavIcon>
          {t("side.signals")}
        </NavItem>
      </NavList>

      <RoleRequired staff={true} permission={permissions.blockEdit}>
        <ConfigSection>
          <BlockConfigDash block={block} />
        </ConfigSection>
      </RoleRequired>

      {signalsEnabled && (
        <SignalSection>
          <Popup
            content={signalsInfo()}
            position={"bottom center"}
            trigger={
              <div>
                <TicketEdit
                  block={block}
                  trigger_props={{
                    disabled: hasDebt,
                    fluid: true,
                    content: t("block.signal"),
                    color: "green",
                    icon: false,
                    size: "medium",
                    basic: false,
                  }}
                />
              </div>
            }
          />
        </SignalSection>
      )}
    </div>
  );
};
